import { useState } from 'react';
import { Calendar, Monitor, Smartphone, Tablet, LogOut, ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../../hooks/useAuth';
import { DATE_RANGES, DEVICE_TYPES } from '../../utils/constants';
import { getInitials } from '../../utils/formatters';

export default function Header() {
  const { user, logout } = useAuth();
  const [dateRange, setDateRange] = useState('7d');
  const [deviceType, setDeviceType] = useState('all');
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [deviceDropdownOpen, setDeviceDropdownOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const currentDateRange = DATE_RANGES.find((r) => r.value === dateRange);
  const currentDevice = DEVICE_TYPES.find((d) => d.value === deviceType);

  const deviceIcons: Record<string, React.ReactNode> = {
    all: <Monitor className="h-4 w-4" />,
    desktop: <Monitor className="h-4 w-4" />,
    mobile: <Smartphone className="h-4 w-4" />,
    tablet: <Tablet className="h-4 w-4" />,
  };

  return (
    <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6">
      <div className="flex items-center gap-3">
        {/* Date Range Picker */}
        <div className="relative">
          <button
            onClick={() => {
              setDateDropdownOpen(!dateDropdownOpen);
              setDeviceDropdownOpen(false);
            }}
            className="btn-secondary gap-2"
          >
            <Calendar className="h-4 w-4" />
            {currentDateRange?.label}
            <ChevronDown className="h-4 w-4" />
          </button>
          {dateDropdownOpen && (
            <div className="absolute left-0 top-full z-10 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
              {DATE_RANGES.map((range) => (
                <button
                  key={range.value}
                  onClick={() => {
                    setDateRange(range.value);
                    setDateDropdownOpen(false);
                  }}
                  className={clsx(
                    'block w-full px-4 py-2 text-left text-sm transition-colors',
                    range.value === dateRange
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-gray-700 hover:bg-gray-50',
                  )}
                >
                  {range.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Device Filter */}
        <div className="relative">
          <button
            onClick={() => {
              setDeviceDropdownOpen(!deviceDropdownOpen);
              setDateDropdownOpen(false);
            }}
            className="btn-secondary gap-2"
          >
            {deviceIcons[deviceType]}
            {currentDevice?.label}
            <ChevronDown className="h-4 w-4" />
          </button>
          {deviceDropdownOpen && (
            <div className="absolute left-0 top-full z-10 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
              {DEVICE_TYPES.map((device) => (
                <button
                  key={device.value}
                  onClick={() => {
                    setDeviceType(device.value);
                    setDeviceDropdownOpen(false);
                  }}
                  className={clsx(
                    'flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors',
                    device.value === deviceType
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-gray-700 hover:bg-gray-50',
                  )}
                >
                  {deviceIcons[device.value]}
                  {device.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* User Profile */}
      <div className="relative">
        <button
          onClick={() => {
            setProfileOpen(!profileOpen);
            setDateDropdownOpen(false);
            setDeviceDropdownOpen(false);
          }}
          className="flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-100"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700">
            {user ? getInitials(user.name) : '?'}
          </div>
          <span className="text-sm font-medium text-gray-700">{user?.name}</span>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </button>
        {profileOpen && (
          <div className="absolute right-0 top-full z-10 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
            <div className="border-b border-gray-100 px-4 py-2">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
